import { z } from 'zod';
import {
  TempControlFacilityType,
  TempControlProject,
  TempControlSizingMethod,
  TempControlValveFamily,
} from '__generated-api__';
import { defaultAllowablePsi } from 'tempcontrol/enums';

const enumValues = <T extends Record<string, string>>(obj: T): [T[keyof T], ...T[keyof T][]] => {
  const values = Object.values(obj) as T[keyof T][];
  return values as [T[keyof T], ...T[keyof T][]];
};

export const facilityTypeSchema = z.enum(enumValues(TempControlFacilityType));
export const sizingMethodSchema = z.enum(enumValues(TempControlSizingMethod));
export const valveFamilySchema = z.enum(enumValues(TempControlValveFamily));

export const fixtureSchema = z.object({
  type: z.number().int().min(0).max(13),
  quantity: z.number().int().min(0),
  flow_rate: z.number().nullable().optional(),
  hot_temp: z.number().nullable().optional(),
  cold_temp: z.number().nullable().optional(),
  desired_temp: z.number().nullable().optional(),
});

export const valveOptionsSchema = z.object({
  piping_package: z.boolean().optional(),
  factory_assembled: z.boolean().optional(),
  cold_water_bypass: z.boolean().optional(),
  wall_mounts: z.boolean().optional(),
  top_supplies: z.boolean().optional(),
  vacuum_breaker: z.boolean().optional(),
  low_temp: z.boolean().optional(),
});

/**
 * Loose, optional-everything schema that backs the single React Hook Form
 * instance powering the entire wizard. Per-step transition validation is
 * enforced by the strict step schemas below.
 */
export const wizardFormSchema = z.object({
  project_name: z.string().max(255).optional().or(z.literal('')),
  facility: facilityTypeSchema.optional(),
  family: valveFamilySchema.optional(),
  method: sizingMethodSchema.optional(),
  allowable_psi: z.number().nullable().optional(),
  fixtures: z.array(fixtureSchema).optional(),
  options: valveOptionsSchema.optional(),
});

export type WizardFormValues = z.infer<typeof wizardFormSchema>;

// ---- Per-step transition schemas ----
//
// Step layout follows the design - 3 numbered wizard steps + a separate
// (un-numbered) results screen. The valve family (digital vs conventional) is
// chosen on the results page, not as its own step:
//
//   Step 1 - Name your project      (project_name)
//   Step 2 - Project configuration  (facility + PSI + add-ons)
//   Step 3 - Add your fixtures       (fixtures)
//   Results - rendered separately, not part of the step counter

export const step1ProjectSchema = z.object({
  project_name: z
    .string({ required_error: 'Project name is required.' })
    .min(1, 'Project name is required.')
    .max(255, 'Project name must be 255 characters or fewer.'),
});
export type Step1Values = z.infer<typeof step1ProjectSchema>;

export const step3ConfigurationSchema = z.object({
  facility: facilityTypeSchema,
  allowable_psi: z
    .number({ invalid_type_error: 'Allowable PSI drop is required.' })
    .min(1, 'Allowable PSI drop must be at least 1.')
    .max(50, 'Allowable PSI drop must be 50 or less.'),
  options: valveOptionsSchema,
});
export type Step3Values = z.infer<typeof step3ConfigurationSchema>;

export const step4FixturesSchema = z.object({
  fixtures: z
    .array(fixtureSchema)
    .min(1, 'Add at least one fixture.')
    .refine(
      (rows) => rows.some((row) => row.quantity > 0),
      'Set a quantity greater than zero on at least one fixture.'
    ),
});
export type Step4Values = z.infer<typeof step4FixturesSchema>;

/**
 * Convert a persisted `TempControlProject` row from the API into the
 * RHF default values shape. Fixtures default to 14 zero-quantity rows
 * (one per `TempControlFixtureType` index) so Step 3 always renders
 * the full table.
 */
export const projectToFormValues = (project?: TempControlProject | null): WizardFormValues => {
  const inputs = project?.inputs ?? {};
  return {
    project_name: project?.project_name ?? '',
    facility: inputs.facility,
    family: inputs.family ?? project?.valve_family,
    method: inputs.method,
    allowable_psi: typeof inputs.allowable_psi === 'number' ? inputs.allowable_psi : defaultAllowablePsi(),
    fixtures: ensureAllFixtureRows(inputs.fixtures ?? []),
    options: {
      piping_package: inputs.options?.piping_package ?? false,
      factory_assembled: inputs.options?.factory_assembled ?? false,
      cold_water_bypass: inputs.options?.cold_water_bypass ?? false,
      wall_mounts: inputs.options?.wall_mounts ?? false,
      top_supplies: inputs.options?.top_supplies ?? false,
      vacuum_breaker: inputs.options?.vacuum_breaker ?? false,
      low_temp: inputs.options?.low_temp ?? false,
    },
  };
};

const ensureAllFixtureRows = (rows: WizardFormValues['fixtures'] = []) => {
  const byType = new Map<number, NonNullable<WizardFormValues['fixtures']>[number]>();
  for (const row of rows) byType.set(row.type, row);
  return Array.from({ length: 14 }, (_, idx) => byType.get(idx) ?? { type: idx, quantity: 0 });
};
